import React from 'react';
import { useTranslation } from 'react-i18next';

import Tracked from '~/components/Tracked';
import useNavigationLinkInteraction from '~/hooks/useNavigationLinkInteraction';
import type { MenuItemProps } from '~/legacy/types';
import { logoIcons } from '~/legacy/utils';
import type { TrackedButtonClickName } from '~/services/gtm.client';

import CategoryList from './CategoryList';
import MenuLink from './MenuLink';

const MenuItem = ({
  link,
  name,
  content,
  slug,
  track,
  pagePosition,
  position = 1
}: MenuItemProps) => {
  const { t } = useTranslation();
  const fireNavigationLink = useNavigationLinkInteraction('meganav');
  const featuredBrandsLabel = t('featuredBrands');
  const viewAllBrandsLabel = t('viewAllBrands');

  return (
    <li className="main-header__nav__top-list-item">
      <Tracked.ExternalLink
        name={name as TrackedButtonClickName}
        href={link}
        variant="none"
        className="main-header__nav__top-list-item--link flex"
        pagePosition="Header"
        onClick={() =>
          fireNavigationLink(link, position, {
            ancestorLabels: [],
            label: name
          })
        }
      >
        {name}
      </Tracked.ExternalLink>
      {content?.render ? (
        <div className="main-header__nav__sub-nav-list">
          <div className="main-header__nav__sub-nav-list--offset"></div>
          <div className="main-header__nav__sub-nav-list--container">
            <div className="content-width pt-8 pb-10 max-h-[calc(100vh-65px)]">
              <div className="flex w-full flex-col">
                {/* Footwear SubMenu Filters */}
                {content?.footwear?.filters.length > 0 && (
                  <div className="flex w-full gap-6">
                    <div className="text-title-sm">
                      {content?.footwear?.title}
                    </div>
                    <div className="flex gap-4">
                      {content?.footwear?.filters.map(
                        (item: MenuItemProps, i: number) => (
                          <React.Fragment key={`${item?.title}/${item?.link}`}>
                            <MenuLink
                              title={item?.title}
                              link={item?.link}
                              track={track}
                              pagePosition={pagePosition}
                              onClick={() =>
                                fireNavigationLink(item?.link, i + 1, {
                                  ancestorLabels: [name],
                                  label: item?.title
                                })
                              }
                            />
                          </React.Fragment>
                        )
                      )}
                    </div>
                  </div>
                )}

                <div className="flex gap-6 items-center mt-6 mb-4">
                  <div className="text-title-sm">{featuredBrandsLabel}</div>
                  {content?.footwear?.viewAll && (
                    <MenuLink
                      link={content?.footwear.viewAll.link}
                      title={viewAllBrandsLabel}
                      onClick={() =>
                        fireNavigationLink(content?.footwear.viewAll.link, 1, {
                          ancestorLabels: [name, featuredBrandsLabel],
                          label: viewAllBrandsLabel
                        })
                      }
                    />
                  )}
                </div>

                {content?.footwear?.featuredBrands && (
                  <div
                    className={`flex w-full justify-between ${
                      content?.footwear?.featuredBrands.path === 'clothing'
                        ? 'mr-10'
                        : ''
                    }`}
                  >
                    {content?.footwear?.featuredBrands.children.map(
                      (item: any, i: number) => {
                        const {
                          children,
                          icon,
                          name: brandName,
                          viewAll,
                          path
                        } = item;
                        const brandHref = viewAll
                          ? `${path}${viewAll?.link || ''}`
                          : undefined;

                        return (
                          <div key={i} className="subNavFeaturedBrands">
                            {icon ? (
                              <Tracked.ExternalLink
                                variant="none"
                                href={brandHref}
                                pagePosition="Header"
                                name={brandName as TrackedButtonClickName}
                                onClick={() =>
                                  brandHref &&
                                  fireNavigationLink(brandHref, i + 1, {
                                    ancestorLabels: [name, featuredBrandsLabel],
                                    label: brandName
                                  })
                                }
                              >
                                <img
                                  alt={brandName}
                                  src={logoIcons[icon]}
                                  loading="lazy"
                                  className="subNavFeaturedBrands__icon"
                                />
                              </Tracked.ExternalLink>
                            ) : (
                              slug
                            )}
                            {children && (
                              <>
                                {viewAll && (
                                  <Tracked.ExternalLink
                                    href={`${path}${viewAll?.link || ''}`}
                                    className="text-title-xs flex items-center h-6 pt-1"
                                    variant="none"
                                    pagePosition="Header"
                                    name={
                                      viewAll.label as TrackedButtonClickName
                                    }
                                    onClick={() =>
                                      fireNavigationLink(
                                        `${path}${viewAll?.link || ''}`,
                                        1,
                                        {
                                          ancestorLabels: [
                                            name,
                                            featuredBrandsLabel,
                                            brandName
                                          ],
                                          label: viewAll.label
                                        }
                                      )
                                    }
                                  >
                                    {viewAll.label}
                                  </Tracked.ExternalLink>
                                )}
                                {children?.map(
                                  (brand: MenuItemProps, j: number) => (
                                    <Tracked.ExternalLink
                                      className="subNavFeaturedBrands__brand"
                                      key={`sub_brand_${j}`}
                                      href={`${path}${brand?.slug}/`}
                                      pagePosition="Header"
                                      variant="none"
                                      name={
                                        brand.name as TrackedButtonClickName
                                      }
                                      onClick={() =>
                                        fireNavigationLink(
                                          `${path}${brand?.slug}/`,
                                          j + (viewAll ? 2 : 1),
                                          {
                                            ancestorLabels: [
                                              name,
                                              featuredBrandsLabel,
                                              brandName
                                            ],
                                            label: brand?.name
                                          }
                                        )
                                      }
                                    >
                                      {brand?.name}
                                    </Tracked.ExternalLink>
                                  )
                                )}
                              </>
                            )}
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
                {content?.footwear?.categories && (
                  <div className="main-header__sub-nav-list__extended-category">
                    {['sport', 'style', 'material', 'colour'].map(
                      (category) =>
                        content?.footwear?.categories?.[category] && (
                          <CategoryList
                            key={category}
                            title={content.footwear.categories[category].title}
                            children={
                              content.footwear.categories[category].children
                            }
                            parentLink={
                              content.footwear.categories[category].link
                            }
                            ancestorLabels={[
                              name,
                              content.footwear.categories[category].title
                            ]}
                          />
                        )
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </li>
  );
};

export default MenuItem;
